import React, { useEffect, useState, useMemo, useCallback } from 'react';

import classNames from 'classnames';

import { calculateRowTotals } from '../../utils/calculations';
import { percentFigure } from '../../utils/percentFigure';
import { getBgClass, getIndentClass } from '../../utils/styleUtils';

import SponsorMilestoneData from './SponsorMilestoneData';
import SponsorMilestoneFields from './SponsorMilestoneFields';

import { formatFigure } from 'Helpers/formatService';

import CheckCircleIcon from 'Assets/icons/circle-check.svg';
import CircleXMark from 'Assets/icons/circle-xmark.svg';
import EditIcon from 'Assets/icons/edit.svg';

export default function MilestoneTypeRow(props) {
    const { type, label, data, periods, indentLevel, contract, maxSponsorMilestonesCount, sponsor, rowTotalValue = 0 } = props;

    // Memoize milestones to avoid recalculation
    const milestones = useMemo(() => {
        return data.milestones ? data.milestones[type] : [];
    }, [data.milestones, type]);

    const [milestonesCount, setMilestonesCount] = useState(milestones.length);
    const [currentMilestones, setCurrentMilestones] = useState(milestones);
    const [totalValue, setTotalValue] = useState(milestones.reduce((sum, milestone) => sum + (milestone.amount || 0), 0));

    // Row-level editing state
    const [isRowEditing, setIsRowEditing] = useState(false);
    const [editedValues, setEditedValues] = useState({});

    // Memoize committed/editable state
    const isCommitted = useMemo(() => !!contract, [contract]);
    const isEditable = useMemo(() => props.canEdit && !isCommitted, [props.canEdit, isCommitted]);

    useEffect(() => {
        // Sort milestones by date to ensure correct chronological order
        const sortedMilestones = [...currentMilestones].sort((a, b) => new Date(a.date) - new Date(b.date));

        setMilestonesCount(sortedMilestones.length);
        setCurrentMilestones(sortedMilestones);
    }, [milestones, periods]);

    useEffect(() => {
        updateTotalAfterMilestonesChanges();
    }, [currentMilestones]);

    // Memoize URL creation
    const saveResourceUrl = useMemo(() => {
        return props.SponsorMilestoneUpdateUrl.replace('SPONSOR_ID', sponsor.id);
    }, [props.SponsorMilestoneUpdateUrl, sponsor.id]);

    // Save resource function with specific milestones (for add milestone to avoid stale state)
    const saveResourceWithMilestones = useCallback(
        (milestonesToSave) => {
            if (!isEditable || isCommitted) return;

            props.setLoading(true);
            let updateUrl = '';
            if (props.milestoneType == 'sponsor') {
                updateUrl = saveResourceUrl;
            }

            axios
                .post(updateUrl, {
                    dates: milestonesToSave.map((milestone) => {
                        return {
                            date: milestone.date,
                            name: milestone.name,
                            description: milestone.description,
                            perc: milestone.percentage,
                        };
                    }),
                    section: type === 'cash' ? 'fixed/cash' : 'fixed/vik',
                })
                .then((response) => {
                    props.setLoading(false);
                    props.setSponsorEditing(null, null, null, null);
                    // props.fetchCentralCashflow();
                })
                .catch((e) => {
                    props.setLoading(false);
                    props.modalShow(true, `Error`, `Request Failed`);
                });
        },
        [isEditable, isCommitted, props, saveResourceUrl, type],
    );

    const saveMilestonePercentage = (event, i, isDeletion = false) => {
        if (!isEditable || isCommitted) return;

        if (event.key == 'Enter') {
            const currentMilestone = data.currentMilestones[i];
            const milestoneIndex = currentMilestones.findIndex((milestone) => {
                const milestoneDate = new Date(milestone.date);
                const periodDate = new Date(currentMilestone.date);
                return milestoneDate.getFullYear() === periodDate.getFullYear() && milestoneDate.getMonth() === periodDate.getMonth();
            });

            // If this is a deletion request or the value is 0, remove the milestone
            if (isDeletion || parseFloat(event.target.value) === 0) {
                if (milestoneIndex !== -1) {
                    const updatedMilestones = [...currentMilestones];
                    updatedMilestones.splice(milestoneIndex, 1);
                    setCurrentMilestones(updatedMilestones);
                    saveResourceWithMilestones(updatedMilestones);
                }
                props.setSponsorEditing(null, null, null, null);
                return;
            }

            // For new milestones or updates, validate the total percentage
            const newValue = parseFloat(event.target.value);
            if (isNaN(newValue) || newValue < 0) {
                props.modalShow(true, 'Invalid percentage', 'Please enter a valid percentage value');
                return;
            }

            let totalPercentage = 0;
            currentMilestones.forEach((element, index) => {
                if (index !== milestoneIndex) {
                    totalPercentage += parseFloat(element.percentage || 0);
                }
            });
            totalPercentage += newValue;

            if (totalPercentage > 100) {
                props.modalShow(true, "Percentage can't be more than 100", "The sum of all the percentages of the milestones can't be greater than 100");
                return;
            }

            const updatedMilestones = [...currentMilestones];

            if (milestoneIndex === -1) {
                // This is a new milestone
                const newMilestone = {
                    id: undefined,
                    date: currentMilestone.date,
                    type: type === 'cash' ? 'fixed/cash' : 'fixed/vik',
                    name: `${label} Milestone ${milestonesCount + 1}`,
                    percentage: newValue,
                    budget_id: 1,
                    created_at: undefined,
                    updated_at: undefined,
                };
                updatedMilestones.push(newMilestone);
            } else {
                // Update existing milestone
                const matchingMilestone = updatedMilestones[milestoneIndex];
                matchingMilestone.percentage = newValue;
                updatedMilestones[milestoneIndex] = matchingMilestone;
            }

            // Sort milestones by date
            updatedMilestones.sort((a, b) => new Date(a.date) - new Date(b.date));

            setCurrentMilestones(updatedMilestones);
            saveResourceWithMilestones(updatedMilestones);
            props.setSponsorEditing(null, null, null, null);
        } else if (event.key == 'Escape') {
            props.setSponsorEditing(null, null, null, null);
        }
    };

    const internalSetEditing = (milestone_index, editType) => {
        if (!isEditable || isCommitted) return;

        props.setSponsorEditing(sponsor.id, milestone_index, editType, type);
    };

    const closeChangeMilestone = () => {
        props.setSponsorEditing(null, null, null, null);
    };

    // Row-level editing functions
    const handleRowEdit = useCallback(() => {
        if (!isEditable || isCommitted) return;

        // Initialize edited values with current milestone percentages
        const initialValues = {};
        data.currentMilestones?.forEach((milestone, index) => {
            if (milestone?.date) {
                const milestonesInThisPeriod = currentMilestones.filter((m) => {
                    const milestoneDate = new Date(m.date);
                    const periodDate = new Date(milestone.date);
                    return milestoneDate.getFullYear() === periodDate.getFullYear() && milestoneDate.getMonth() === periodDate.getMonth();
                });

                if (milestonesInThisPeriod.length > 0) {
                    initialValues[index] = milestonesInThisPeriod[0].percentage || '';
                } else {
                    initialValues[index] = '';
                }
            }
        });

        setEditedValues(initialValues);
        setIsRowEditing(true);
    }, [isEditable, isCommitted, data.currentMilestones, currentMilestones]);

    const handleRowSave = useCallback(() => {
        if (!isEditable || isCommitted) return;

        // Validate total percentage
        let totalPercentage = 0;
        const validValues = {};

        Object.entries(editedValues).forEach(([index, value]) => {
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue > 0) {
                validValues[index] = numValue;
                totalPercentage += numValue;
            }
        });

        if (totalPercentage > 100) {
            props.modalShow(true, "Percentage can't be more than 100", "The sum of all the percentages of the milestones can't be greater than 100");
            return;
        }

        // Create updated milestones array
        let updatedMilestones = [...currentMilestones];

        // Remove existing milestones for ALL periods being edited (both valid and invalid/empty values)
        Object.keys(editedValues).forEach((index) => {
            const periodIndex = parseInt(index);
            const periodDate = data.currentMilestones[periodIndex]?.date;
            if (periodDate) {
                updatedMilestones = updatedMilestones.filter((milestone) => {
                    const milestoneDate = new Date(milestone.date);
                    const targetDate = new Date(periodDate);
                    return !(milestoneDate.getFullYear() === targetDate.getFullYear() && milestoneDate.getMonth() === targetDate.getMonth());
                });
            }
        });

        // Add new milestones
        var currentMilestoneCount = 1;
        Object.entries(validValues).forEach(([index, value]) => {
            const periodIndex = parseInt(index);
            const periodDate = data.currentMilestones[periodIndex]?.date;
            if (periodDate) {
                const newMilestone = {
                    date: periodDate,
                    name: `${label} Milestone ${currentMilestoneCount++}`,
                    percentage: value,
                    type: type === 'cash' ? 'fixed/cash' : 'fixed/vik',
                };
                updatedMilestones.push(newMilestone);
            }
        });

        // Sort milestones by date
        updatedMilestones = updatedMilestones.sort((a, b) => new Date(a.date) - new Date(b.date));

        setCurrentMilestones(updatedMilestones);
        setMilestonesCount(updatedMilestones.length);
        setIsRowEditing(false);
        setEditedValues({});

        // Save to backend
        saveResourceWithMilestones(updatedMilestones);
    }, [isEditable, isCommitted, editedValues, currentMilestones, data.currentMilestones, props, label, milestonesCount, type, saveResourceWithMilestones]);

    const handleRowCancel = useCallback(() => {
        setIsRowEditing(false);
        setEditedValues({});
    }, []);

    // Respond to Edit All trigger (monotonic by version)
    useEffect(() => {
        const t = props.editAllTrigger;
        if (!t?.version) return;
        if (!isEditable || isCommitted) return;

        const matchesTarget = t.target === 'all' || t.target === 'sponsors';
        if (!matchesTarget) return;

        setIsRowEditing(!!t.on);
    }, [props.editAllTrigger?.version, isEditable, isCommitted]);

    const handleValueChange = useCallback((index, value) => {
        setEditedValues((prev) => ({
            ...prev,
            [index]: value,
        }));
    }, []);

    // Create currentMilestones for this specific type by filtering the milestone amounts
    const typeCurrentMilestones = [];
    if (data.currentMilestones) {
        for (let i = 0; i < maxSponsorMilestonesCount; i++) {
            // For type-specific rows, we need to calculate what portion of each period belongs to this type
            let typeName = '';
            let typeAmount = 0;
            let typePercentage = 0;
            const currentMilestone = data.currentMilestones[i];

            if (currentMilestone) {
                // Calculate the proportion of this type's milestones that fall in this period
                const periodDate = new Date(currentMilestone.date);
                const typeMilestonesInPeriod = currentMilestones.filter((milestone) => {
                    const milestoneDate = new Date(milestone.date);
                    return milestoneDate.getFullYear() === periodDate.getFullYear() && milestoneDate.getMonth() === periodDate.getMonth();
                });

                typeAmount = typeMilestonesInPeriod.reduce((sum, milestone) => sum + (milestone.amount || 0), 0);

                // For committed milestones, get percentage from the milestones object (not currentMilestones)
                if (isCommitted && data.milestones && data.milestones[type] && data.milestones[type].length > 0) {
                    // Find milestone that matches this period
                    const matchingMilestone = data.milestones[type].find((milestone) => {
                        const milestoneDate = new Date(milestone.date);
                        return milestoneDate.getFullYear() === periodDate.getFullYear() && milestoneDate.getMonth() === periodDate.getMonth();
                    });
                    typePercentage = matchingMilestone ? matchingMilestone.percentage || 0 : 0;
                    typeName = matchingMilestone ? matchingMilestone.name || '' : '';
                }
            }

            // Always add milestone to structure, even if amount is 0
            const milestoneData = {
                amount: typeAmount,
                date: currentMilestone ? currentMilestone.date : '',
            };

            // Add percentage for committed milestones
            if (isCommitted) {
                milestoneData.percentage = typePercentage;
                milestoneData.name = typeName;
            }

            typeCurrentMilestones.push(milestoneData);
        }
    }

    const getTextClass = (level) => {
        if (level > 2 && isCommitted) {
            return '!tw-font-bold';
        }
    };

    const updateTotalAfterMilestonesChanges = () => {
        setTotalValue(currentMilestones.reduce((sum, milestone) => sum + (rowTotalValue > 0 ? (rowTotalValue * (milestone.percentage || 0)) / 100 : 0), 0));
    };

    const totals = useMemo(() => {
        // While editing (regardless of committed flag), preview totals should reflect in-progress inputs
        if (isRowEditing && editedValues && data.currentMilestones) {
            const byYearMonth = new Map();

            currentMilestones.forEach((m) => {
                const d = new Date(m.date);
                const key = `${d.getFullYear()}-${d.getMonth()}`;
                byYearMonth.set(key, { ...m });
            });

            Object.entries(editedValues).forEach(([index, value]) => {
                const periodIndex = parseInt(index);
                const periodDate = data.currentMilestones[periodIndex]?.date;
                if (!periodDate) return;

                const d = new Date(periodDate);
                const key = `${d.getFullYear()}-${d.getMonth()}`;
                const num = parseFloat(value);

                if (!isNaN(num) && num > 0) {
                    const existing = byYearMonth.get(key) || { date: periodDate };
                    existing.percentage = num;
                    // Calculate live amount for this milestone
                    existing.amount = rowTotalValue > 0 ? (rowTotalValue * num) / 100 : 0;
                    byYearMonth.set(key, existing);
                } else if (byYearMonth.has(key)) {
                    const existing = byYearMonth.get(key);
                    existing.percentage = 0;
                    existing.amount = 0;
                    byYearMonth.set(key, existing);
                }
            });

            return calculateRowTotals(Array.from(byYearMonth.values()));
        }

        // If committed and not editing, use committed milestone data
        if (isCommitted) {
            const committed = data?.milestones?.[type] || [];
            return calculateRowTotals(committed);
        }

        // Default: use current (uncommitted) milestones
        return calculateRowTotals(currentMilestones);
    }, [currentMilestones, editedValues, isRowEditing, data.currentMilestones, data?.milestones, type, isCommitted, rowTotalValue]);

    return (
        <tr className={isEditable ? '!tw-bg-white' : getBgClass(indentLevel, 'sponsor')}>
            <td className={getIndentClass(indentLevel)}></td>
            <td className="tw-p-4">
                <div className="tw-font-medium">{label}</div>
            </td>
            <td className={classNames('tw-p-4 tw-text-right tw-border-r-2', getTextClass(indentLevel))}>{formatFigure(rowTotalValue)}</td>

            {isEditable
                ? // Editable milestones for uncommitted
                  [...Array(maxSponsorMilestonesCount)].map((e, i) => {
                      return (
                          <React.Fragment key={`${type}_milestone_${i}`}>
                              <SponsorMilestoneFields
                                  key={`${props.keyPrefix || ''}${type}_milestone_${sponsor.id}_${i}`}
                                  sponsor={{
                                      ...sponsor,
                                      currentMilestones: (() => {
                                          const structured = [];
                                          // Map milestones to their corresponding period columns
                                          if (data.currentMilestones && data.currentMilestones[i]) {
                                              const periodDate = new Date(data.currentMilestones[i].date);
                                              const milestonesInThisPeriod = currentMilestones.filter((milestone) => {
                                                  const milestoneDate = new Date(milestone.date);
                                                  return (
                                                      milestoneDate.getFullYear() === periodDate.getFullYear() &&
                                                      milestoneDate.getMonth() === periodDate.getMonth()
                                                  );
                                              });

                                              // If there are milestones in this period, use the first one for editing
                                              if (milestonesInThisPeriod.length > 0) {
                                                  const milestone = milestonesInThisPeriod[0];
                                                  const amount = rowTotalValue > 0 ? (rowTotalValue * (milestone.percentage || 0)) / 100 : 0;
                                                  structured[i] = {
                                                      ...milestone,
                                                      amount: amount,
                                                  };
                                              }
                                          }
                                          return structured;
                                      })(),
                                      id: sponsor.id,
                                  }}
                                  editingSponsorId={props.editingSponsorId}
                                  editingType={props.editingType}
                                  editingMilestoneId={props.editingMilestoneId}
                                  editingMilestoneType={props.editingMilestoneType}
                                  internalSetEditing={internalSetEditing}
                                  saveMilestonePercentage={saveMilestonePercentage}
                                  closeChangeMilestone={closeChangeMilestone}
                                  milestoneId={i}
                                  milestoneType={type}
                                  keyPrefix={props.keyPrefix || ''}
                                  canEdit={props.canEdit}
                                  // Row-level editing props
                                  isRowEditing={isRowEditing}
                                  editedValues={editedValues}
                                  handleValueChange={handleValueChange}
                                  rowTotalValue={rowTotalValue}
                              />
                          </React.Fragment>
                      );
                  })
                : // Read-only milestones for committed
                  [...Array(maxSponsorMilestonesCount)].map((e, i) => {
                      return (
                          <React.Fragment key={`${type}_milestone_${i}`}>
                              <SponsorMilestoneData
                                  key={`${props.keyPrefix || ''}${type}_${i}`}
                                  milestones={typeCurrentMilestones}
                                  milestoneId={i}
                                  keyPrefix={props.keyPrefix || ''}
                                  amountOnly={false}
                                  isCommitted={isCommitted}
                              />
                          </React.Fragment>
                      );
                  })}
            <td className="tw-p-4 tw-text-right">{percentFigure(totals.totalPercentage)}</td>
            <td className="tw-p-4 tw-text-right">{formatFigure(totals.totalValue)}</td>
            <td className="tw-p-4 tw-text-center">
                {isEditable && !isCommitted && (
                    <div className="tw-flex tw-justify-center tw-gap-2">
                        {!isRowEditing ? (
                            <button
                                type="button"
                                onClick={handleRowEdit}
                                className="tw-p-2 tw-rounded tw-bg-blue-500 tw-text-white tw-hover:bg-blue-600 tw-transition-colors"
                                title="Edit row"
                            >
                                <EditIcon className="tw-w-4 tw-h-4" />
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={handleRowSave}
                                    className="tw-p-2 tw-rounded tw-bg-green-500 tw-text-white tw-hover:bg-green-600 tw-transition-colors"
                                    title="Save changes"
                                >
                                    <CheckCircleIcon className="tw-w-4 tw-h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={handleRowCancel}
                                    className="tw-p-2 tw-rounded tw-bg-gray-500 tw-text-white tw-hover:bg-gray-600 tw-transition-colors"
                                    title="Cancel changes"
                                >
                                    <CircleXMark className="tw-w-4 tw-h-4" />
                                </button>
                            </>
                        )}
                    </div>
                )}
            </td>
        </tr>
    );
}
